import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Eye, DollarSign, TrendingUp } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammInvestorService } from '../services/pammService';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getProfitColor,
  exportToCSV,
} from '../utils';
import type { PAMMInvestor } from '../types';
import { PAMM_INVESTOR_STATUS_OPTIONS } from '../constants';
import { PAMM_ROUTES } from '../../../constants/routeConstants';
import type { DataTableColumn } from '../../../components/ui/data-table';

export default function Investors() {
  const navigate = useNavigate();
  const [investors, setInvestors] = useState<PAMMInvestor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pammInvestorService.getAll();
      setInvestors(data);
    } catch {
      setError('Failed to load investors.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const totalInvested = investors.reduce((s, i) => s + i.investedAmount, 0);
  const totalProfit = investors.reduce((s, i) => s + i.profit, 0);
  const activeCount = investors.filter(i => i.status === 'active').length;

  const columns: DataTableColumn<PAMMInvestor>[] = [
    {
      id: 'name',
      header: 'Investor',
      accessor: row => (
        <div>
          <p className="font-medium text-xs">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
    },
    {
      id: 'account',
      header: 'Account',
      accessor: row => (
        <span className="text-xs font-mono">{row.accountNumber}</span>
      ),
    },
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => (
        <span className="text-xs truncate max-w-[120px] block">
          {row.fundName}
        </span>
      ),
      sortable: true,
      sortValue: row => row.fundName,
    },
    {
      id: 'invested',
      header: 'Invested',
      accessor: row => (
        <span className="text-xs font-medium">
          {formatCurrency(row.investedAmount)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.investedAmount,
    },
    {
      id: 'value',
      header: 'Current Value',
      accessor: row => (
        <span className="text-xs font-medium">
          {formatCurrency(row.currentValue)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.currentValue,
    },
    {
      id: 'profit',
      header: 'Profit/Loss',
      accessor: row => (
        <div>
          <p className={`text-xs font-semibold ${getProfitColor(row.profit)}`}>
            {formatCurrency(row.profit)}
          </p>
          <p className={`text-xs ${getProfitColor(row.profitPercentage)}`}>
            {formatPercent(row.profitPercentage)}
          </p>
        </div>
      ),
      sortable: true,
      sortValue: row => row.profit,
    },
    {
      id: 'kyc',
      header: 'KYC',
      accessor: row => <StatusChip status={row.kycStatus} size="sm" />,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'joined',
      header: 'Joined',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.joinDate)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: row => (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2"
          onClick={e => {
            e.stopPropagation();
            navigate(`${PAMM_ROUTES.PAMM_INVESTOR_DETAILS}/${row.id}`);
          }}
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Investors" />
        <LoadingSkeleton type="cards" cols={4} />
        <LoadingSkeleton type="table" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Investors" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Investors"
        description="Manage all PAMM fund investors and their portfolios"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Investors"
          value={investors.length}
          icon={Users}
        />
        <StatsCard title="Active" value={activeCount} icon={Users} />
        <StatsCard
          title="Total Invested"
          value={formatCurrency(totalInvested, 'USD', true)}
          icon={DollarSign}
        />
        <StatsCard
          title="Total Profit"
          value={formatCurrency(totalProfit, 'USD', true)}
          icon={TrendingUp}
        />
      </div>

      {investors.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No Investors Yet"
          description="Investors will appear here once they join a PAMM fund."
        />
      ) : (
        <DataTable
          columns={columns}
          data={investors}
          searchValue={searchValue}
          searchPlaceholder="Search investors..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          filterFields={[
            {
              id: 'status',
              label: 'Status',
              options: PAMM_INVESTOR_STATUS_OPTIONS,
            },
          ]}
          activeFilters={activeFilters}
          showColumnVisibility={true}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(investors, 'pamm_investors')}
          onRowClick={row =>
            navigate(`${PAMM_ROUTES.PAMM_INVESTOR_DETAILS}/${row.id}`)
          }
        />
      )}
    </div>
  );
}
